import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Gauge, BadgeCheck, HardDrive, Users, AlertTriangle, ArrowRight, Gift } from "lucide-react";
import { api, fmtGB } from "../lib/api";
import { fmtStorage } from "../lib/planStyles";
import { GlassCard, PageHeader, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sys, setSys] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/saas/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    };
    load();
    const t = setInterval(load, 20000);  // métricas ao vivo: storage, banda e fila atualizam sozinhos
    return () => clearInterval(t);
  }, [user.role]);

  useEffect(() => {
    if (user.role !== "super_admin") return;
    const load = () => api.get("/admin/system").then((r) => setSys(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user.role]);

  if (!stats) return <div className="flex justify-center py-24"><Spinner /></div>;

  const trialDaysLeft = stats.plan_expires_at ? Math.max(0, Math.ceil((new Date(stats.plan_expires_at).getTime() - Date.now()) / 86400000)) : 0;

  const isAdmin = user.role === "super_admin";
  const storageUsed = Number(stats.storage_used_gb) || 0;
  const storageQuota = Number(stats.plan_storage_gb) || 0;
  const storagePct = !isAdmin && storageQuota > 0 ? Math.min(100, Math.round((storageUsed / storageQuota) * 100)) : null;
  const expiryLabel = stats.plan_expires_at ? new Date(stats.plan_expires_at).toLocaleDateString("pt-PT") : null;

  const cards = [
    {
      label: "Assinatura",
      value: isAdmin ? "Ilimitada" : (stats.plan_active ? stats.plan_name : "Inativa"),
      sub: isAdmin ? "Acesso total da plataforma" : stats.plan_active && expiryLabel ? `Válida até ${expiryLabel}` : stats.plan_active ? "Plano ativo" : "Nenhum plano ativo",
      icon: BadgeCheck, accent: stats.plan_active || isAdmin ? "text-emerald-400" : "text-rose-400", testid: "stat-plan",
    },
    {
      label: isAdmin ? "Storage NVMe (plataforma)" : "Storage NVMe",
      value: isAdmin ? fmtStorage(stats.storage_total_gb ?? 0) : storageQuota ? `${fmtGB(storageUsed)} de ${fmtStorage(storageQuota)}` : fmtGB(storageUsed),
      sub: isAdmin ? "Soma de todas as contas" : storagePct != null ? `${storagePct}% da cota utilizada` : "Sem cota definida",
      icon: HardDrive, accent: "text-amber-400", testid: "stat-storage",
    },
    {
      label: "Banda trafegada",
      value: fmtGB(stats.bandwidth_gb),
      sub: "Streaming ilimitado, sem desconto por GB",
      icon: Gauge, accent: "text-cyan-400", testid: "stat-bandwidth",
    },
    {
      label: "Clientes (acessos)",
      value: stats.accesses ?? 0,
      sub: "Credenciais Xtream/M3U geradas",
      icon: Users, accent: "text-emerald-400", testid: "stat-accesses",
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Visão geral"
        title={`Olá, ${user.name.split(" ")[0]}`}
        subtitle="Seus bots, acervo e armazenamento em tempo real."
      />

      {sys && (sys.cpu_percent > 80 || sys.mem_percent > 85) && (
        <div data-testid="server-alert" className="rounded-xl border border-rose-400/40 bg-rose-400/10 px-5 py-4 flex items-center gap-3 anim-fade-up">
          <AlertTriangle size={18} className="text-rose-400 shrink-0" />
          <p className="text-sm text-rose-200 leading-relaxed">
            <b>Servidor no limite:</b> CPU em {Math.round(sys.cpu_percent)}% · RAM em {Math.round(sys.mem_percent)}% ({sys.mem_used_gb}/{sys.mem_total_gb} GB)
            {sys.jobs_running > 0 ? ` · ${sys.jobs_running} importação(ões) rodando` : ""}.
            Detalhes em Admin → Status do Servidor.
          </p>
        </div>
      )}

      <a
        data-testid="sync-promo-banner"
        href="https://wxsyncvods.shop"
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-lg border border-cyan-500/25 bg-cyan-500/5 backdrop-blur-md px-5 py-4 flex items-center gap-4 anim-fade-up transition-colors hover:bg-cyan-500/10"
      >
        <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center shrink-0">
          <RefreshCw size={18} className="text-cyan-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold flex items-center gap-2 flex-wrap">
            Sincronizador
            <span className="text-[9px] font-mono2 uppercase tracking-widest text-cyan-300 border border-cyan-500/30 bg-cyan-500/10 rounded-full px-2 py-0.5">Parceiro</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">Precisa de sincronização? O Sincronizador sobe conteúdo automaticamente para a sua plataforma — acesse <span className="text-cyan-300">wxsyncvods.shop</span></p>
        </div>
        <ArrowRight size={16} className="text-cyan-300 shrink-0" />
      </a>

      <TutorialHint
        id="dashboard"
        title="Bem-vindo ao CineBot"
        steps={[
          "Crie a sua instância em Meus Bots — tudo o que sobe cai na pasta raiz da sua conta na nuvem (organize na aba Pastas).",
          "Suba conteúdo pela aba Uploads: arquivo do aparelho (sua internet) ou link/YouTube/lista M3U (internet do servidor, pode fechar a página).",
          "Crie usuários de cliente na aba Acessos (usuário, senha, conexões simultâneas) e envie o link M3U/Xtream para seus clientes.",
          "Sua assinatura cobre o armazenamento — o streaming é ilimitado, sem desconto por GB.",
        ]}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <GlassCard key={c.label} data-testid={c.testid} className={`glass-hover p-5 anim-fade-up anim-delay-${(i % 4) + 1} relative overflow-hidden border-t-2 border-t-amber-500/60`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-9 h-9 rounded-lg border border-[#232D42] bg-[#0C101A] flex items-center justify-center shrink-0 ${c.accent}`}>
                <c.icon size={16} />
              </div>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 leading-tight">{c.label}</p>
            </div>
            <p className="font-display text-xl sm:text-2xl font-bold text-white leading-tight">{c.value}</p>
            <p className="text-[11px] text-slate-500 mt-1.5 truncate" title={c.sub}>{c.sub}</p>
          </GlassCard>
        ))}
      </div>

      {user.role !== "super_admin" && !stats.plan_active && stats.plan_expires_at && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-400/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 anim-fade-up" data-testid="plan-expired-banner">
          <AlertTriangle size={18} className="text-rose-400 shrink-0" />
          <p className="flex-1 text-sm text-rose-200 leading-relaxed">
            <b>{user.plan_id === "plan_trial" ? "Seu teste grátis acabou" : "Sua assinatura venceu"}</b> — o streaming dos seus links está bloqueado (erro 402) e os uploads pausados. {user.plan_id === "plan_trial" ? "Assine um plano para continuar usando a plataforma." : "Renove para reativar tudo em segundos."}
          </p>
          <Link data-testid="expired-renew-btn" to="/app/billing" className="btn-neon px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2 shrink-0">
            {user.plan_id === "plan_trial" ? "Assinar agora" : "Renovar agora"} <ArrowRight size={15} />
          </Link>
        </div>
      )}

      {user.role !== "super_admin" && stats.plan_active && user.plan_id === "plan_trial" && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 anim-fade-up" data-testid="trial-active-banner">
          <Gift size={18} className="text-emerald-300 shrink-0" />
          <p className="flex-1 text-sm text-emerald-200 leading-relaxed">
            <b>Teste grátis ativo:</b> {trialDaysLeft} {trialDaysLeft === 1 ? "dia restante" : "dias restantes"} · 30 GB de espaço NVMe. Assine um plano para não perder o acesso ao fim do teste.
          </p>
          <Link data-testid="trial-plans-btn" to="/app/billing" className="btn-ghost-dark px-6 py-3 rounded-xl text-sm font-semibold text-white inline-flex items-center gap-2 shrink-0">
            Ver planos <ArrowRight size={15} />
          </Link>
        </div>
      )}

      {user.role !== "super_admin" && !stats.plan_active && !stats.plan_expires_at && (
        <GlassCard className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 anim-fade-up" data-testid="plan-cta-card">
          <div>
            <h3 className="font-display text-white font-semibold">Nenhum plano ativo</h3>
            <p className="text-sm text-slate-400 mt-1">Assine um plano de armazenamento para subir conteúdo e transmitir com streaming ilimitado.</p>
          </div>
          <Link data-testid="dashboard-plans-btn" to="/app/billing" className="btn-neon px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2 shrink-0">
            Ver planos <ArrowRight size={15} />
          </Link>
        </GlassCard>
      )}

    </div>
  );
}
